import { useState, useEffect } from "react";
import { Download, Smartphone, X } from "lucide-react";
import { usePWAInstall } from "../../lib/usePWAInstall";
import { IOSInstallSheet } from "./IOSInstallSheet";
import { Button } from "../ui/Button";

const DISMISSAL_KEY = "soorgaa_install_banner_dismissed";
const VISIT_COUNT_KEY = "soorgaa_visit_count";
const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

export function InstallBanner() {
  const { canInstall, isInstalled, showIOSPrompt, setShowIOSPrompt, install } =
    usePWAInstall();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isInstalled) return;

    try {
      // Check 14-day dismissal
      const dismissedAt = localStorage.getItem(DISMISSAL_KEY);
      if (dismissedAt && Date.now() - Number(dismissedAt) < FOURTEEN_DAYS_MS) {
        return;
      }

      // Check visit count (after 2nd visit)
      const currentVisits = Number(localStorage.getItem(VISIT_COUNT_KEY) || "0") + 1;
      localStorage.setItem(VISIT_COUNT_KEY, String(currentVisits));

      if (currentVisits >= 2 && canInstall) {
        setVisible(true);
      }
    } catch {
      // In private browsing mode or storage disabled
    }
  }, [canInstall, isInstalled]);

  function handleDismiss() {
    setVisible(false);
    try {
      localStorage.setItem(DISMISSAL_KEY, String(Date.now()));
    } catch {}
  }

  if (!visible || isInstalled || !canInstall) {
    return (
      <IOSInstallSheet
        isOpen={showIOSPrompt}
        onClose={() => setShowIOSPrompt(false)}
      />
    );
  }

  return (
    <>
      <div className="relative rounded-lg border border-primary/25 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
        <div className="flex items-start gap-3 min-w-0 pr-6 sm:pr-0">
          <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Smartphone size={20} />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-sm text-foreground">
              Install Soorgaa on your device
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Fast full-screen access, smoother study sessions, and offline notes.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
          <Button
            variant="primary"
            size="sm"
            onClick={install}
            icon={<Download size={14} />}
            className="font-bold"
          >
            Install app
          </Button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss banner"
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <IOSInstallSheet
        isOpen={showIOSPrompt}
        onClose={() => setShowIOSPrompt(false)}
      />
    </>
  );
}
